<template>
  <div
    class="side-panel ml-5"
    :class="{
      'side-panel-open': sidePanelOpen,
      'side-panel-closed': !sidePanelOpen
    }"
    :style="{ height: sidePanelOpen ? 'initial' : '100%' }"
  >
    <!-- Title Level -->
    <div class="top-level">
      <div
        class="toggle-panel-button-background"
        :class="sidePanelOpen ? 'toggle-panel-button-background-open' : 'w-full'"
      >
        <Button
          @click="togglePanelUpdateEvent"
          v-tooltip.left="sidePanelOpen ? 'Close Material List' : 'Open Material List'"
          :icon="sidePanelOpen ? 'pi pi-angle-right' : 'pi pi-angle-left'"
          class="toggle-panel-button p-button-rounded p-button-secondary"
        />
      </div>
      <h2 v-if="sidePanelOpen" class="main-title header">Material Lookup</h2>
    </div>

    <!-- Selected Level -->
    <ul v-if="sidePanelOpen" class="selected-list">
      <li class="selected-list-li">
        <div
          class="selected-material-description"
        >
          <b>SELECTED:</b>
          <span
            v-if="selectedMaterial !== null"
            class="selected-material-number"
          >
            {{ selectedMaterial?.number || "" }}
          </span>
        </div>
      </li>

      <li class="selected-list-li">
        <b>EQUIPMENT TYPE:</b>
        <span class="equipment-type-label">{{
          customizingLineItem ? typePresentation(customizedMaterial?.line_item_type || '') : typePresentation(computedLineItemType)
        }}</span>
      </li>
    </ul>

    <h4 style="display: none">
      {{ materialEquipmentType }}
      {{ foundEtc }}
    </h4>

    <div
      v-if="sidePanelOpen"
      class="old-search-parent"
      style="display: flex; justify-content: flex-end"
    >
      <label
        for="basicSearch"
        class="mr-1"
        style="font-size: 14px; line-height: 24px"
        >Advanced Search</label
      >
      <input
        id="basicSearch"
        data-cy="old-search-checkbox"
        v-model="filters.basicSearch"
        type="checkbox"
        class="p-checkbox p-checkbox-box mr-5"
        input-id="basicSearch"
        binary
        :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
        @change="goToFirstPage"
      />
    </div>

    <!-- Material Filters Level -->
    <div class="material-filters">
      <div
        v-if="sidePanelOpen"
        class="material-search mr-2"
        :style="{ width: materialSearchWidth }"
      >
        <a
          v-tooltip.top="searchHelpText"
          class="search search-label block"
        >
          SEARCH:
        </a>
        <InputText
          id="materialSearch"
          data-cy="material-search-input"
          v-model="filters.materialSearchInput"
          class="padded-section-item"
          :class="{ 'p-invalid': false }"
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          placeholder="Search Materials"
          @input="onMaterialSearchInput"
          type="search"
        />
      </div>
      <div
        v-if="sidePanelOpen && filters.basicSearch"
        class="material-search-exclude">
        <a
          v-tooltip.top="'Optional. Advanced search excludes comma-separated terms from the full-text material search.'"
          class="exclude search-label block"
        >EXCLUDE:</a>
        <InputText
          v-if="sidePanelOpen && filters.basicSearch"
          id="materialSearchExclude"
          data-cy="exclude-input"
          v-model="filters.excludeInput"
          class="padded-section-item"
          :class="{ 'p-invalid': false }"
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          placeholder="Exclude"
          @input="onMaterialSearchInput"
        />
      </div>
    </div>

    <div v-if="sidePanelOpen" class="material-filters product-hierarchy-filter">
      <div class="material-search">
        <a
          v-tooltip.top="'Optional. Filter by an SK equipment type; its product hierarchies are sent to the search behind the scenes.'"
          class="search search-label block"
        >EQUIPMENT TYPE:</a>
        <Dropdown
          id="productHierarchySearch"
          v-model="filters.productHierarchyInput"
          data-cy="product-hierarchy-filter"
          :options="productHierarchyChoices"
          option-value="value"
          option-label="label"
          :filter="true"
          filter-placeholder="Search equipment types"
          placeholder="Any Equipment Type"
          class="padded-section-item w-full"
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          @change="goToFirstPage"
        />
      </div>
    </div>

    <!-- Material Type (single, mutually-exclusive selection) -->
    <div v-if="sidePanelOpen" class="material-type-row">
      <label for="materialTypeSelect" class="material-type-label">Material Type</label>
      <Dropdown
        id="materialTypeSelect"
        v-model="filters.materialTypeSelection"
        data-cy="material-type-filter"
        :options="materialTypeChoices"
        option-value="value"
        option-label="label"
        class="material-type-dropdown w-full"
        :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
        @change="goToFirstPage"
      />
    </div>

    <!-- Boolean Filters Level -->
    <div v-if="sidePanelOpen" class="flex flex-wrap padded-section">
      <div class="flex align-items-center">
        <input
          id="inStockFilter"
          v-model="filters.inStockFilter"
          type="checkbox"
          class="p-checkbox p-checkbox-box"
          input-id="inStockFilter"
          binary
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          @change="goToFirstPage"
        />
        <label
          v-tooltip.top="'Requires material inventory with available stock at the selected sales plant.'"
          for="inStockFilter"
          class="mr-3"
        >In Stock</label>
      </div>

      <div class="flex align-items-center">
        <input
          id="cableFilter"
          v-model="filters.cableFilter"
          type="checkbox"
          class="p-checkbox p-checkbox-box"
          input-id="cableFilter"
          binary
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          @change="goToFirstPage"
        />
        <label
          v-tooltip.top="'Requires available cable serial inventory at the selected sales plant.'"
          for="cableFilter"
          class="mr-3"
        >Cable Available</label>
      </div>

      <div class="flex align-items-center">
        <input
          id="pricedFilter"
          v-model="filters.pricedFilter"
          type="checkbox"
          class="p-checkbox p-checkbox-box"
          input-id="pricedFilter"
          binary
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          @change="goToFirstPage"
        />
        <label
          v-tooltip.top="'Requires matching ERP pricing for the selected country, price list or contract, and pricing date.'"
          for="pricedFilter"
          class="mr-3"
        >Has Price</label>
      </div>
      <div class="flex align-items-center">
        <input
          id="showStandardOffering"
          v-model="filters.standardOfferingFilter"
          data-cy="show-standard-offering-filter"
          type="checkbox"
          class="p-checkbox p-checkbox-box"
          input-id="showStandardOffering"
          binary
          :disabled="
            quoteIsWonOrRejected ||
            customizingLineItem ||
            showRequiredInfoMessage ||
            selectedQuoteLineItem === null
          "
          @change="goToFirstPage"
        >
        <label for="showStandardOffering" class="mr-3">
          Standard Offering
        </label>
      </div>
      <div class="flex align-items-center">
        <input
          id="allPlantsFilter"
          v-model="filters.allPlantsFilter"
          type="checkbox"
          class="p-checkbox p-checkbox-box"
          input-id="allPlantsFilter"
          binary
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          @change="goToFirstPage"
        />
        <label
          v-tooltip.top="'By default, only materials extended to the selected sales plant are shown. Check to search materials across all plants.'"
          for="allPlantsFilter"
          class="mr-3"
        >Search All Plants</label>
      </div>
    </div>

    <!-- User Help Text - Missing Headers -->
    <ul v-if="sidePanelOpen" style="padding-left: 5px; padding-right: 5px">
      <div v-if="showRequiredInfoMessage" class="required-info">
        <h5>
          Go to 'Sales Info' tab and fill out the required fields to use Material Lookup:
          {{ missingMaterialSearchFields.join(", ") || "country, price list, sales plant, pricing date" }}
        </h5>
      </div>
    </ul>

    <div
      v-if="sidePanelOpen && materialSearchError"
      class="material-search-error"
    >
      {{ materialSearchError }}
    </div>

    <h4 v-if="sidePanelOpen" class="material-lookup-title">Material Results</h4>
    <!-- Table Level -->
    <div
      v-if="!customizingLineItem && filteredMaterials.length > 0"
    >
      <DataTable
        v-if="sidePanelOpen"
        id="materialLookupTable"
        v-model:selection="selectedMaterial"
        stripedRows
        :value="filteredMaterials"
        :loading="isMaterialLookupLoading"
        :lazy="true"
        tableStyle="min-width: 100%; border: 2px solid var(--border-main);"
        :class="quoteIsWonOrRejected || showRequiredInfoMessage ? 'p-disabled' : ''"
        :selectionMode="quoteIsWonOrRejected ? undefined : 'single'"
        @row-unselect="materialTableUnselect"
        @sort="onSortChange($event)"
      >
        <!--
        @page="pageChange($event)"
        paginator
        paginator-position="bottom"
        paginator-template="CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink RowsPerPageDropdown"
        responsive-layout="stack"
        current-page-report-template="Page {currentPage}"
        :rows="filters.pageSize"
        -->
        <Column
          sortable
          class="data-column"
          field="number"
          header="MATERIAL NUMBER"
          headerStyle="width: 8rem;"
          data-cy="number-column"
        >
          <template #body="slotProps">
            <span>
              {{ slotProps?.data?.number }}
              <span
                v-tooltip.top="'Standard Offering'"
                  :class="{
                    'standard-offering-checkbox pi pi-check-circle': (filters.standardOfferingFilter && !materialsLoading)
                  }"
              ></span>
            </span>
          </template>
        </Column>
        <Column 
          sortable 
          field="description"
          class="data-column" 
          header="DESCRIPTION"
          data-cy="description-column"
        >
          <template #body="slotProps">
            <span>{{
              displayDescription(
                slotProps?.data?.long_description ||
                  slotProps?.data?.description
              )
            }}</span>
          </template>
        </Column>
        <Column
          sortable
          class="data-column"
          field="inventory.available"
          header="STOCK"
          headerStyle="width: 5rem;"
        >
          <template #body="slotProps">
            <span>{{
              displayStock(slotProps?.data)
            }}</span>
          </template>
        </Column>
        <Column
          sortable
          class="data-column"
          field="pricing.price"
          header="PRICE"
          headerStyle="width: 6rem;"
        >
          <template #body="slotProps">
            <span v-if="slotProps?.data?.pricing?.price">{{
              helpers.formatCurrency(
                slotProps?.data?.pricing?.price,
                quote?.currency
              )
            }}</span>
            <!-- A loading spinner for individual missing contract prices.
             Network throttle to see better results in local dev. -->
            <span v-if="!slotProps?.data?.pricing?.price">
              <i 
                v-if="
                  isContractSelected
                  && isContractPriceMissing(slotProps?.data)
                  && (materialsLoading || materialsLoadingCountry)
                "
                class="pi pi-spin pi-spinner" 
              />
            </span>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Custom Line Item Interface -->
    <div v-if="customizingLineItem">
      <DataTable
        v-if="sidePanelOpen"
        id="customLineItemInterface"
        tableStyle="min-width: 100%; border: 2px solid var(--border-main);"
        :loading="isMaterialLookupLoading"
      >
        <Column class="data-column" header="Custom Line Item" />
        <template #empty>
          <form class="grid" @submit.prevent>
            <fieldset class="col grid" :disabled="false">
              <FormLabel text="Type" class="col-4">
                <Dropdown
                  v-model="customizedMaterial.line_item_type"
                  :options="customTypes"
                  class="w-full"
                />
              </FormLabel>
              <FormLabel text="Price" class="col-4">
                <span class="input-symbol-usd" :class="{ 'p-disabled': false }">
                  <input
                    v-model="customizedMaterial.custom_price"
                    class="w-full p-inputtext"
                    type="number"
                    placeholder="0"
                    autocomplete="off"
                    @keydown="checkValidNumberInput($event, true, true)"
                  />
                </span>
              </FormLabel>
              <FormLabel text="Qty" class="col-4">
                <input
                  v-model="customizedMaterial.quantity"
                  class="w-full p-inputtext"
                  type="number"
                  style="min-width: none !important"
                  autocomplete="off"
                  @keydown="checkValidNumberInput($event, false, false)"
                  @paste="sanitizePastedNumberInput"
                />
              </FormLabel>
              <FormLabel class="col-12" text="Description">
                <input
                  v-model="customizedMaterial.long_description"
                  class="w-full p-inputtext"
                  placeholder="e.g. TBD - PROTO, 456, DS9, 13:1 PMM"
                />
              </FormLabel>
              <FormLabel class="col-6">
                <Button
                  class="p-button-danger w-full ml-0 mr-0 pl-1 pr-1"
                  label="Cancel"
                  @click="customizingLineItem = !customizingLineItem"
                />
              </FormLabel>
              <FormLabel class="col-6">
                <Button
                  class="p-button-info w-full"
                  label="Add to Quote"
                  @click="addToQuoteClickCustomMaterial($event, actions.custom)"
                />
              </FormLabel>
            </fieldset>
          </form>
        </template>
      </DataTable>
    </div>

    <!-- When results are empty, or user is searching and results are being retrieved, show this -->
    <div
      v-if="!customizingLineItem && filteredMaterials.length === 0"
    >
      <DataTable
        v-if="sidePanelOpen"
        id="materialLookupTable"
        :value="filteredMaterials"
        tableStyle="min-width: 100%; border: 2px solid var(--border-main);"
        :loading="isMaterialLookupLoading"
      >
        <template #empty>
          <div v-if="isMaterialLookupLoading">
            <div v-if="filters?.materialSearchInput">
              Retrying search with <b>{{ filters?.materialSearchInput }}</b
              >.
            </div>
            <div v-else>
              <!-- If users fully backspace a search with no results -->
              Retrieving default materials.
            </div>
          </div>
          <div v-else-if="rawPageResultCount > 0">
            No matching materials on this page. Try another page or refine your
            search.
          </div>
          <div v-else>
            <div>
              No results found for <b>{{ filters.materialSearchInput }}</b
              >.
            </div>
            <br />
            <div>Please try another search.</div>
          </div>
        </template>
        <Column class="data-column" header="MATERIAL NUMBER" sortable></Column>
        <Column class="data-column" header="DESCRIPTION" sortable></Column>
        <Column class="data-column" header="STOCK" sortable></Column>
        <Column class="data-column" header="PRICE" sortable></Column>
      </DataTable>
    </div>

    <!-- (HTML from PrimVue Paginator) -->
    <div
      v-if="sidePanelOpen"
      class="p-paginator p-component"
      data-pc-name="paginator"
      data-pc-section="root"
      pv_id_71=""
    >
      <span
        v-tooltip.top="!customizingLineItem ? 'Go to First Page' : ''"
        class="paginator-controls"
        :class="{
          'paginator-button-disabled' : customizingLineItem,
          'p-paginator-current': !customizingLineItem,
        }"
        data-pc-section="current"
        aria-live="polite"
        :style="{ cursor: !customizingLineItem ? 'pointer' : 'default' }"
        @click="!customizingLineItem ? goToFirstPage() : null"
      >
        Page {{ filters.page }} of {{ totalPages }}
      </span>

      <!-- Prev -->
      <button
        class="paginator-controls p-paginator-element p-link"
        :class="{
          'paginator-button-disabled' : prevPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage,
          'p-paginator-prev paginator-button' : !(prevPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage),
        }"
        type="button"
        aria-label="Previous Page"
        :disabled="prevPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
        @click="prevPage"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="p-icon p-paginator-icon"
        >
          <path
            d="M8.75 11.185C8.65146 11.1854 8.55381 11.1662 8.4628 11.1284C8.37179 11.0906 8.28924 11.0351 8.22 10.965L4.72 7.46496C4.57955 7.32433 4.50066 7.13371 4.50066 6.93496C4.50066 6.73621 4.57955 6.54558 4.72 6.40496L8.22 2.93496C8.36095 2.84357 8.52851 2.80215 8.69582 2.81733C8.86312 2.83252 9.02048 2.90344 9.14268 3.01872C9.26487 3.134 9.34483 3.28696 9.36973 3.4531C9.39463 3.61924 9.36303 3.78892 9.28 3.93496L6.28 6.93496L9.28 9.93496C9.42045 10.0756 9.49934 10.2662 9.49934 10.465C9.49934 10.6637 9.42045 10.8543 9.28 10.995C9.13526 11.1257 8.9448 11.1939 8.75 11.185Z"
            fill="currentColor"
          ></path>
        </svg>
      </button>

      <!-- Next -->
      <button
        class="paginator-controls p-paginator-element p-link"
        :class="{
          'paginator-button-disabled' : nextPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage,
          'p-paginator-next paginator-button' : !(nextPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage),
        }
        "
        type="button"
        aria-label="Next Page"
        :disabled="nextPageDisabled || quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
        @click="nextPage"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="p-icon p-paginator-icon"
        >
          <path
            d="M5.25 11.1728C5.14929 11.1694 5.05033 11.1455 4.9592 11.1025C4.86806 11.0595 4.78666 10.9984 4.72 10.9228C4.57955 10.7822 4.50066 10.5916 4.50066 10.3928C4.50066 10.1941 4.57955 10.0035 4.72 9.86283L7.72 6.86283L4.72 3.86283C4.66067 3.71882 4.64765 3.55991 4.68275 3.40816C4.71785 3.25642 4.79932 3.11936 4.91585 3.01602C5.03238 2.91268 5.17819 2.84819 5.33305 2.83149C5.4879 2.81479 5.64411 2.84671 5.78 2.92283L9.28 6.42283C9.42045 6.56346 9.49934 6.75408 9.49934 6.95283C9.49934 7.15158 9.42045 7.34221 9.28 7.48283L5.78 10.9228C5.71333 10.9984 5.63193 11.0595 5.5408 11.1025C5.44966 11.1455 5.35071 11.1694 5.25 11.1728Z"
            fill="currentColor"
          ></path>
        </svg>
      </button>
      <span
        class="paginator-controls"
        :class="{'paginator-button-disabled' : customizingLineItem}"
      >
        {{ getNumberOfResults || 0 }}
        {{
          filters.inStockFilter || filters.cableFilter
            ? "potential results"
            : "results"
        }}
      </span>
      <span v-if="!customizingLineItem" class="paginator-controls page-size-control">
        Per page
        <select
          v-model.number="filters.pageSize"
          :disabled="quoteIsWonOrRejected || showRequiredInfoMessage || isMaterialLookupLoading"
          @change="goToFirstPage"
        >
          <option
            v-for="pageSize in pageSizeChoices"
            :key="pageSize"
            :value="pageSize"
          >
            {{ pageSize }}
          </option>
        </select>
      </span>
    </div>
    <!-- (End of HTML from PrimeVue Paginator) -->

    <!-- Quantity + Choose Type Dropdown -->
    <div class="choose-type-flex">
      <FormLabel
        v-if="sidePanelOpen"
        text="Choose Type"
        class="choose-type-label col-6"
        :disabled="disableChooseType()"
        :class="{ invisible: false }"
      >
        <Dropdown
          v-model="chosenType"
          :options="chosenTypeChoices"
          option-value="value"
          option-label="label"
          class="choose-type-dropdown w-full"
          :disabled="disableChooseType()"
          :class="{ invisible: false }"
        />
      </FormLabel>

      <!-- Add Quantity - Dropdown (serialized) -->
      <FormLabel
        v-if="isMaterialSerialized() && sidePanelOpen"
        v-tooltip.top="
          selectedMaterial?.serialized
            ? '[Serialized] Quantity 1, Many Line Items'
            : '[Unserialized] Any Quantity, 1 Line Item'
        "
        text="Quantity"
        class="add-quantity-label col-2"
      >
        <Dropdown
          v-model="addQuantityWritable"
          :options="addQuantityChoices"
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          class="choose-type-dropdown w-full"
          @keydown="checkValidNumberInput($event, false, false)"
          @paste="sanitizePastedNumberInput"
        />
      </FormLabel>

      <!-- Add Quantity - Input (unserialized/cable) -->
      <FormLabel
        v-if="!isMaterialSerialized() && sidePanelOpen"
        v-tooltip.top="
          selectedMaterial?.serialized
            ? '[Serialized] Quantity 1, Many Line Items'
            : '[Unserialized] Any Quantity, 1 Line Item'
        "
        for="addQuantityElement"
        text="Quantity"
        class="add-quantity-label"
      >
        <InputText
          id="addQuantityElement"
          v-model="addQuantityWritable"
          :options="addQuantityChoices"
          :disabled="quoteIsWonOrRejected || customizingLineItem || showRequiredInfoMessage"
          class="add-quantity-dropdown"
          placeholder="0"
          type="text"
          autocomplete="off"
          @keydown="checkValidNumberInput($event, false, false)"
          @paste="sanitizePastedNumberInput"
        />
      </FormLabel>
    </div>

    <!-- Buttons Level -->
    <div v-if="sidePanelOpen" class="add-to-quote-buttons">
      <Button
        :disabled="disableAddToQuote()"
        @click="addToQuoteClick($event, actions.add)"
      >
        <!-- Use non-breaking spaces to center (text-align was not working) -->
        Add To Quote
      </Button>
      <Button
        :disabled="disableReplaceSelected()"
        @click="addToQuoteClick($event, actions.replace)"
      >
        Replace Selected
      </Button>
      <Button
        :disabled="disableCustom()"
        class="text-center ml-auto"
        @click="addCustomLineClick($event)"
        
      >
        Add Custom Line
      </Button>
    </div>

    <MaterialPricingChart
      :materialRef="selectedMaterial"
      :sidePanelOpen="sidePanelOpen"
      :pricingData="pricingData"
      :enable="!!selectedMaterial"
    >
    </MaterialPricingChart>

    <!-- Recovery Level -->

    <!-- Slider Column -->
    <i
      v-if="sidePanelOpen"
      ref="sliderRef"
      class="vertical-slider-area"
      @mousedown="startResize"
    >
    </i>

    <!-- Slider -->
    <i
      v-if="sidePanelOpen"
      class="slider-icon pi pi-arrow-left"
      draggable="true"
    >
    </i>

    <!-- Closed Label -->
    <div v-if="!sidePanelOpen" class="side-panel-closed-header">
      Material&nbsp;Lookup
    </div>

    <!-- Totals, Directions, Quote Notes, Comments Section -->
    <div v-if="sidePanelOpen && quote">
      <div class="panel-data-header">Totals</div>
      <DataView class="panel-data-view" :value="quote?.quote_line_items" dataKey="quote?.id">
        <template #list="quote_line_items"> <!-- eslint-disable-line -->
          <div class="panel-data-view-inner">
            <!--

            <div class="panel-data-subheader">Recovery</div>
            <div class="panel-data-row">
              <div>
                <span class="panel-data-data">
                  0 MONTHS
                </span>
                <br>COST RECOVERY
              </div>
              <div>
                <span class="panel-data-data">
                  $0
                </span>
                <br>PRICE RECOVERY
              </div>
            </div>

            -->

            <div class="panel-data-subheader" v-if="_isJobTypeWellTestRental">Well Test</div>
            <div class="panel-data-row" v-if="_isJobTypeWellTestRental">
              <div>
                <span class="panel-data-data">
                  {{ wellTestTotalMargin.toFixed(4) }}%
                </span>
                <br>CONSUM. MARGIN
              </div>
              <div>
                <span class="panel-data-data">
                  ${{ wellTestTotalCost.toFixed(2) }}
                </span>
                <br>CONSUM. COST
              </div>
              <div>
                <span class="panel-data-data">
                  ${{ wellTestSubtotal.toFixed(2) }}
                </span>
                <br>SUB-TOTAL
              </div>
            </div>

            
            <div class="panel-data-subheader">Sale & Trade</div>
            <div class="panel-data-row">
              <div>
                <span class="panel-data-data">
                  {{ saleAndTradeTotalMargin.toFixed(4) }}%
                </span>
                <br />TOTAL MARGIN
              </div>
              <div>
                <span class="panel-data-data">
                  ${{ saleAndTradeTotalCost.toFixed(2) }}
                </span>
                <br />TOTAL COST
              </div>
              <div>
                <span class="panel-data-data">
                  ${{ saleAndTradeSubtotal.toFixed(2) }}
                </span>
                <br />SUB-TOTAL
              </div>
            </div>
            <div class="panel-data-subheader">CREDITS & DISCOUNTS</div>
            <div class="panel-data-row">
              <div>
                <span class="panel-data-data"> ${{ credits.toFixed(2) }} </span>
                <br />TOTAL CREDITS
              </div>
              <div>
                <span class="panel-data-data"> ${{ discounts.toFixed(2) }} </span>
                <br />TOTAL DISCOUNTS
              </div>
            </div>
          </div>
        </template>
      </DataView>

      <FormLabel class="panel-text-label" text="Directions">
        <textarea
          v-model="advancedSearch.directions"
          class="panel-text-area"
          :style="{ color: notesColor, backgroundColor: notesBackgroundColor }"
        >
        </textarea>
      </FormLabel>

      <FormLabel class="panel-text-label" text="External Notes">
        <textarea
          v-model="advancedSearch.comments"
          class="panel-text-area"
          :style="{ color: notesColor, backgroundColor: notesBackgroundColor }"
        >
        </textarea>
      </FormLabel>

      <FormLabel
        class="panel-text-label"
        text="Internal Notes"
        style="margin-bottom: 20px"
      >
        <textarea
          v-model="advancedSearch.note"
          class="panel-text-area"
          :style="{ color: notesColor, backgroundColor: notesBackgroundColor }"
        >
        </textarea>
      </FormLabel>
    </div>
  </div>
</template>

<script lang="ts" setup>
// setup: executes every time an instance of the component is created
import {
  reactive,
  ref,
  computed,
  onMounted,
  onUnmounted,
  PropType,
  watch,
  toRefs,
  ComputedRef,
  provide
} from "vue";
import { useRouter } from "vue-router";
import { getQuoteAdjustments, getQuoteLineUnitPrice } from "@/utils/quotePricing";
import { watchOnce } from "@vueuse/core";
import {
  type SkMaterial,
  useSkMaterialsQuery,
  useSkMaterialsPaginatedQuery,
  useUpdateQuoteByPkMutation,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
  AllQuoteFieldsFragment as QuoteType,
  Quote_Sk_Job_Type_Dataset_Enum,
  Quote_Line_Item_Category_Dataset_Enum as Category,
  useGetQuoteLineItemsQuery,
  Quote_Line_Items_Select_Column,
  Quote_Source_Dataset_Enum as QuoteSource
} from "gql/api";

import { storeToRefs } from "pinia";

import type {
  MaterialQueryParams,
  SidePanelFilters,
  UIMaterial,
  SpyglassType,
} from "@/components/quotes/types";
import MaterialPricingChart from "@/components/quotes/MaterialPricingChart.vue";
import {
  createQueryString,
  buildMaterialSearchQueryParams,
  fixDate,
  statuses,
  line_item_order,
  checkValidNumberInput,
  sanitizePastedNumberInput,
  isEmpty,
  notNull,
  sidePanelActions as actions,
  LineItemType,
  isQuantityOneMaterial,
  isJobTypeWellTestRental,
  isConsumableWellTestLineItem,
  cleanNull,
  filterUnsellableMaterials,
  isContractPricing,
  sortInStockFirst,
} from "@/utils/quoteHelpers";
import * as helpers from "@/utils/quoteHelpers";
import { useLineItemStore } from "@/store/line_item_store";
import { useSidePanelStore } from "@/store/side_panel";
import { useSettingsStore } from "@/store/settings";
import {
  useEquipmentTypeConversions,
} from "@/composables/quote/side-panel/useEquipmentTypeConversions";

import FormLabel from "@/components/FormLabel.vue";
import DataView from "primevue/dataview";
import type { DataTableSortEvent } from "primevue/datatable";

const router = useRouter(); // eslint-disable-line

const { executeMutation: updateQuote } = useUpdateQuoteByPkMutation();

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType>,
    default: null
  },
  advancedSearch: {
    type: Object,
    default: null
  },
  sidePanelOpen: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false,
    required: false
  },
  isMissingRequiredMaterialData: {
    type: Boolean,
    default: false,
    required: true
  }
});

// get data from store
const {
  selectedDescription,
  selectedQuoteLineItem,
  isSelectedQliFromScenario,
  isValidSpyglassEquipment,
} = storeToRefs(useLineItemStore());
const { addQuantity } = storeToRefs(useSidePanelStore());

// set data in store
const { setUserManualSearch, setPanelWidth, setAddQuantity, resetAddQuantity } =
  useSidePanelStore();
const { getSpyglassEquipmentId, getSpyglassEquipmentString } =
  useLineItemStore();

const {
  quote,
  advancedSearch,
  sidePanelOpen,
  isMissingRequiredMaterialData
} = toRefs(props);

const settingsStore = useSettingsStore();
const materialSearchLoading = ref(false);
const isMaterialLookupLoading = computed(() => props.loading || materialSearchLoading.value);

const isContractSelected = computed(() => !!advancedSearch.value?.contractSearch?.number);
const isContractPriceMissing = (material: SkMaterial) => {
  return isContractSelected.value && material.pricing === null;
}
// SK-5234: canonical S4 pricing returns the condition type as an object, so a
// strict `=== 2` check never matched and contract prices got the 60% margin.
const isContractType = (material: SkMaterial) => isContractPricing(material.pricing);

const chosenType = ref<LineItemType | undefined | "">(""); // TODO: check areas of code switching between undefined and ""
let userSearchedForMaterial = ref<boolean>(false);
const sortField = ref("number");
const sortOrder = ref(1); // default to ascending

const selectedEquipmentId = ref<string | null>(null);
const selectedEquipmentString = ref<SpyglassType | null>(null);

const materialTableUnselect = () => {
  resetAddQuantity();
  chosenType.value = "";

  // Reset selected equipment refs
  selectedEquipmentId.value = null;
  selectedEquipmentString.value = null;
}

// usage: quoteLineItemsWithEquipment.value?.quote_line_items

const createChosenTypeChoices = () => {
  const choices = line_item_order.map(line_item_type => {
    return {
      value: line_item_type,
      label: helpers.getLineItemTypeLabelCamel(line_item_type)
    };
  });

  return choices;
};

const onSortChange = (e: DataTableSortEvent) => {
  const default_order = "number";

  const fieldMap = {
    number: default_order,
    description: "description",
    "inventory.available": "materialinventory__available_quantity",
    "pricing.price": "pricing__price",
    __typename: "SKMaterial"
  };

  type FieldMapKeyType = keyof typeof fieldMap;
  // We always get sortField back as a string, but typescript is
  // too smart for its own good it knows sometimes it's a callback
  // that returns a string (depending on the component used etc.)
  // so simply make sure it's a string before using it via key access
  // But it doesn't accept any arbitrary string, so need to type the
  // key with the keys of fieldMap (typescript is wild)
  const key = (typeof e.sortField === "string") ? e.sortField : "";
  const apiField = fieldMap[key as FieldMapKeyType] || default_order;
  sortField.value = apiField;
  sortOrder.value = e.sortOrder || 1;

  // sort change should always take us back to page 1
  filters.page = 1;

  runFetchMaterials();
};


const chosenTypeChoices = createChosenTypeChoices();

// Mutually-exclusive material-type options for the Material Type dropdown.
const materialTypeChoices = [
  { value: "all", label: "All" },
  { value: "sale_finished", label: "Sale / Service + Finished Goods" },
  { value: "small_parts", label: "Small Parts" },
  { value: "customer_owned", label: "Customer Owned" },
  { value: "rental", label: "Rental" },
  { value: "trade_in", label: "Trade-In" },
];

const typePresentation = (val: string) => {
  const choice = chosenTypeChoices.find(choice => choice.value === val);

  return choice ? choice.label : "";
};

// create writable computed properly to get/set from store to v-model
const addQuantityWritable = computed({
  get() {
    return addQuantity.value;
  },
  set(qty) {
    setAddQuantity(qty);
  }
});

// Set default quantity
addQuantityWritable.value = 1;

const quantityMax = 50;
const addQuantityChoices = [...Array(quantityMax).keys()].slice(1); // remove zero

const isMaterialSerialized = () => {
  return isQuantityOneMaterial(
    selectedMaterial.value || {},
    equipmentTypeConversions.value?.getSKEquipmentTypeConversions || []
  );
};

const customizingLineItem = ref<boolean>(false);
const customTypes = ref<Array<string>>(["service", "---"].concat(line_item_order));

const customizedMaterial = ref<UIMaterial>({
  line_item_type: "service",
  custom_price: 0,
  quantity: 1,
  long_description: "",
  is_custom: true,
  number: "custom",
  __typename: 'SKMaterial',
}); // keep the number (erp_material_number) as custom so we have a temporary way to identify custom line items

const pageSizeChoices = [5, 10, 20];
defineExpose({
  handleNewDescription(desc: string) {
    if (!userSearchedForMaterial.value && !selectedMaterial.value) {
      filters.materialSearchInput = desc ?? "";

      // Query 1
      runFetchMaterials()
    }
  },
  handleNewMaterialNumber(materialNumberInput: string) {
    clearSearchTimeout();
    filters.materialSearchInput = materialNumberInput ?? "";
    filters.page = 1;
    userSearchedForMaterial.value = materialNumberInput !== "";
    setUserManualSearch(materialNumberInput !== "");
    runFetchMaterials(materialNumberInput);
  },
  /**
   * Refresh material search when users select the contract.
   * This will reflect with correct prices.
   * (i.e., contract price if present, country fallback if not present)
   */
  handleNewContract() {
    /**
     * If needed, run nextTick before calling this.
     * It will ensure the correct/updated contract value on advancedSearch is used.
     */
    runFetchMaterials();
  }
});

const displayDescription = (description: string) => {
  // replace "," with ", "
  return description && description.includes(",")
    ? description.split(",").join(", ")
    : description;

  // take out spaces (include tooltip, line will be flat!)
  // return (
  //  description &&
  //  description.includes(" ") ? description.split(" ").join("") : description
  // )
};

const goToFirstPage = () => {
  clearSearchTimeout();
  filters.page = 1;

  runFetchMaterials();
};

const nextPageDisabled = computed(() => {
  return (
    (getNumberOfResults.value <= 0) ||
    (filters.page === totalPages.value)
  )
});

const prevPageDisabled = computed(() => {
  return filters.page === 1;
});

const missingHeadersDisable = (contract_optional = true) => {
  const country_code =
    advancedSearch?.value?.wellAddressSearch?.address?.region?.country?.code;
  const contract_number = contract_optional
    ? true
    : advancedSearch?.value?.contractSearch.number;
  const price_list_number = advancedSearch?.value?.priceListSearch.number;
  const pricing_date = fixDate(advancedSearch?.value?.pricingDate);

  return (
    !country_code || !contract_number || !price_list_number || !pricing_date
  );
};

const disableAddToQuote = () => {
  const mat_empty = isEmpty(selectedMaterial.value)

  return (
    mat_empty ||
    missingHeadersDisable() ||
    showRequiredInfoMessage.value ||
    quoteIsWonOrRejected.value ||
    isMaterialLookupLoading.value
  );
};


const disableReplaceSelected = () => {
  // selectedMaterial holds the material currently selected in the side panel
  const mat_empty = isEmpty(selectedMaterial.value)

  // selectedDescription holds the quote line item currently selected in the main data table
  const desc_empty = isEmpty(selectedDescription.value)

  return (
    mat_empty ||
    desc_empty ||
    missingHeadersDisable() ||
    showRequiredInfoMessage.value ||
    quoteIsWonOrRejected.value ||
    isMaterialLookupLoading.value
  );
};

const disableCustom = () => {
  return (
    showRequiredInfoMessage.value ||
    quoteIsWonOrRejected.value ||
    isMaterialLookupLoading.value ||
    customizingLineItem.value
  )
}

const disableChooseType = () => {
  const mat_empty = isEmpty(selectedMaterial.value)

  return (
    mat_empty ||
    disableCustom() ||
    missingHeadersDisable()
  )
}

const nextPage = () => {
  // right now, there is no way to know before hand how many pages
  // the api has available, so maybe our best bet is to just stop them
  // from incrementing once the list of materials has no data (at which points
  // they can only go prev).
  // Use the raw (pre-filter) page count so a page that is fully removed by the
  // quote-side unsellable filter does not block paging to the next page.
  if (rawPageResultCount.value !== 0 && !isMaterialLookupLoading.value) {
    clearSearchTimeout();
    filters.page += 1;
    runFetchMaterials();
  }
};

const prevPage = () => {
  if (filters.page > 1 && !isMaterialLookupLoading.value) {
    clearSearchTimeout();
    filters.page -= 1;
    runFetchMaterials();
  }
};

const addCustomLineClick = (e: MouseEvent) => {
  customizingLineItem.value = !customizingLineItem.value;

  selectedMaterial.value = null;
};

const addToQuoteClick = (
  e: Event,
  action: string
) => {
  /* If you are here and wondering how this function adds a quote line item,
     you would want to know that this function triggers an emit event that
     runs a function on the parent, EditQuote, called updateSelectedMaterial,
     which updated a ref defined there called `selectedMaterialRef`, when that ref
     is updated it is passed down into DetailsDataTable and the quote line item
     is added there.

     e: button click event
     action: add, replace, custom
  ***/
  if (!quote.value?.id) {
    e.preventDefault();
  }

  const _line_item_type = action === actions.custom
    ? customizedMaterial?.value?.line_item_type || LineItemType.UNCATEGORIZED
    : chosenType?.value || computedLineItemType?.value || LineItemType.UNCATEGORIZED

  triggerSelectedMaterialUpdate(
    selectedMaterial.value,
    action,
    _line_item_type
  );

  if (action !== actions.custom) {
    // Reset the 'Choose Type' to default to selected type (for next mat selection)
    chosenType.value = "";
    // Delete our retroactively added line_item_type property from selectedMaterial
    delete selectedMaterial?.value?.line_item_type;
  }

  if (action === actions.replace) {
    // If the quote line item is replaced, it is gone. Null it to disable the button
    selectedDescription.value = null;
  }

  /* TODO: We need a PR to disable the action buttons until the promises of
  adding/replacing materials have finished resolving in the DetailsDataTable.vue
  component, then the below line can be removed.

  While it will still technically work with the below line removed, there is
  potential to spam the add/replace button, leading to unpredictable consequences
  with responsivness and race conditions with asynchronous promises. So, we need
  to a way to determine when the add/replace actions have fully finished resolving
  from DetailsDataTable.vue before we can remove the below line.
  */
  selectedMaterial.value = null;
};

const addToQuoteClickCustomMaterial = (e: Event, action: string) => {
  /* Just using this as a wrapper for addToQuoteClick, specifically for custom material stuff
     More stuff may need to be done with custom stuff, not sure yet */
  customizingLineItem.value = !customizingLineItem.value; // toggle the panel closed, users want the panel closed after an item is added

  if (customizedMaterial.value.line_item_type === "service") {
    customizedMaterial.value.category = Category.Service;
    customizedMaterial.value.line_item_type = LineItemType.UNCATEGORIZED;
  } else {
    customizedMaterial.value.category = Category.Equipment;
    chosenType.value = undefined;
  }

  switch (customizedMaterial.value.line_item_type) {
    case LineItemType.PROTECTORS_SEALS:
      customizedMaterial.value.line_item_type = LineItemType.SEAL;
      break;
    case LineItemType.TRANSFORMER:
      customizedMaterial.value.line_item_type = LineItemType.TRANSFORMER;
      break;
  }

  if (selectedMaterial.value === null) {
    selectedMaterial.value = {} as UIMaterial;
  }

  selectedMaterial.value = customizedMaterial.value;
  addToQuoteClick(e, action);
};

const selectedMaterial = ref<UIMaterial | null>(null);

// TODO: everywhere that materialSearchInput is reset, must also reset excludeInput
// resetMaterialInputs()
const filters = reactive<SidePanelFilters>({
  materialSearchInput: "",
  excludeInput: "",
  productHierarchyInput: "",
  basicSearch: false,
  materialTypeSelection: "sale_finished",
  inStockFilter: false,
  pricedFilter: false,
  cableFilter: false,
  standardOfferingFilter: false,
  allPlantsFilter: false,
  page: 1,
  pageSize: 20
});

// Summary: if it has a relation with a spyglass equipment, it came from scenario create quote
//
// Quote line items that have an erp material number are assumed to have been replaced
// But the only way the quote line item gets a valid value for one of these `spyglass_`
// equipment fields is that it came from a scenario that had an equipment association
const {
  data: quoteLineItemsWithEquipment,
  executeQuery: fetchQuoteLineItemsWithEquipment,
} = useGetQuoteLineItemsQuery({
  pause: !filters.standardOfferingFilter || !selectedEquipmentId.value,
  variables: computed(() => {
    return {
      limit: 100,
      offset: 0,
      distinct_on: [Quote_Line_Items_Select_Column.ErpMaterialNumber],
      where: {
        erp_material_number: { _is_null: false },
        _or: [
          { spyglass_cable: { _eq: selectedEquipmentId.value } },
          { spyglass_intake: { _eq: selectedEquipmentId.value } },
          { spyglass_motor_lead_extension: { _eq: selectedEquipmentId.value } },
          { spyglass_motor: { _eq: selectedEquipmentId.value } },
          { spyglass_pump: { _eq: selectedEquipmentId.value } },
          { spyglass_seal: { _eq: selectedEquipmentId.value } },
          { spyglass_sensor: { _eq: selectedEquipmentId.value } },
          { spyglass_step_down_transformer: { _eq: selectedEquipmentId.value } },
          { spyglass_step_up_transformer: { _eq: selectedEquipmentId.value } },
          { spyglass_switchboard: { _eq: selectedEquipmentId.value } },
          { spyglass_variable_speed_drive: { _eq: selectedEquipmentId.value } },
        ],
      },
    };
  })
});


const materialSearchWidth = computed<string>(() => {
  return filters.basicSearch ? "60%" : "95%";
});

const searchHelpText = computed(() => {
  if (filters.basicSearch) {
    return "Advanced search uses comma-separated full-text terms and optional Exclude terms.";
  }

  return "Default search matches material number prefixes, description, and long description.";
});

const showRequiredInfoMessage = computed(() => isMissingRequiredMaterialData.value);

const missingMaterialSearchFields = computed(() => {
  const missingFields: string[] = [];

  if (!advancedSearch?.value?.wellAddressSearch?.address?.region?.country?.code) {
    missingFields.push("country");
  }

  if (!advancedSearch?.value?.priceListSearch?.number) {
    missingFields.push("price list");
  }

  if (!advancedSearch?.value?.salesDistrictSearch?.id) {
    missingFields.push("sales plant");
  }

  if (!advancedSearch?.value?.pricingDate) {
    missingFields.push("pricing date");
  }

  return missingFields;
});

const displayStock = (material: SkMaterial) => {
  if (filters.cableFilter) {
    return helpers.twoDecimals(material?.cable_inventory?.available);
  }

  return helpers.twoDecimals(material?.inventory?.available);
};

const queryParams = computed((): MaterialQueryParams => {
  return buildMaterialSearchQueryParams({
    advancedSearch: advancedSearch.value,
    filters,
    maxItems: filters.pageSize,
    quote: quote.value,
    quoteLineItemsWithEquipment: quoteLineItemsWithEquipment?.value?.quote_line_items || [],
    sortField: sortField.value,
    sortOrder: sortOrder.value,
  }) as MaterialQueryParams;
});

// watch(queryParams, () => {
  // (newParams, oldParams)
  // new idea: since this computed drives the query params,
  // watch query params and when they change just convert the
  // object to query params and add to this current route.
  // Ex. router.push({ path: 'register', query: { plan: 'private' }})
  // router.push({ query: queryParams.value })
// });

watch(selectedQuoteLineItem, (newSelectedItem, oldSelectedItem) => {
  if (newSelectedItem === null) {
    // quote line item is unselected
    filters.standardOfferingFilter = false

    // note: materials are fetched on quote line item selection
    // because the selected description state in pinia changes
    runFetchMaterials()
  }

  // When selected line item changes (regardless if same type), need to sync filters
  // note: watch runs before computed (after line selected)
  selectedEquipmentId.value = getSpyglassEquipmentId()
  selectedEquipmentString.value = getSpyglassEquipmentString()

  // Default the (now mutually-exclusive) material-type selection based on the
  // selected equipment. Sensors use Small Parts (ZSCP); everything else starts
  // on standard Sale/Service + Finished Goods. The user can then explicitly
  // switch to Customer Owned / Rental / Trade-In to narrow results.
  filters.materialTypeSelection =
    getSpyglassEquipmentString() === "sensor" ? "small_parts" : "sale_finished";
});

watchOnce(quote, () => {
  /** This watcher gets materials for users' convenience on:
   * 1) Page initial load
   * 2) Save as New
   *
   * Using watchOnce instead of watch saves UI time/responsiveness
   * by not triggering the search on:
   * 1) save (the same results come back anyway)
   * 2) quote line item delete
   * 3) quote line item reorder
   */
  runFetchMaterials();
});

// Required Sales Info (country, price list, sales plant, pricing date) can
// populate after this panel mounts (the quote / sales info loads
// asynchronously). The material queries are paused and driven imperatively, so
// trigger a fetch when the required data first becomes available — otherwise
// the initial fetch (above) returns early and the table stays empty until the
// user interacts. This replaces the previous reactive pause-flip behavior.
watch(isMissingRequiredMaterialData, (isMissing, wasMissing) => {
  if (wasMissing && !isMissing) {
    runFetchMaterials();
  }
});

const materialDebounceTime = 800;
let searchTimeout: ReturnType<typeof setTimeout> | null = null;
let materialFetchSequence = 0;
const materialSearchError = ref("");

const clearSearchTimeout = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
    searchTimeout = null;
  }
};

const hasManualSearchInput = () => {
  return !!(
    filters.materialSearchInput?.trim() ||
    filters.excludeInput?.trim() ||
    filters.productHierarchyInput?.trim()
  );
};

const onMaterialSearchInput = () => {
  selectedMaterial.value = null;
  filters.page = 1;
  userSearchedForMaterial.value = hasManualSearchInput();
  setUserManualSearch(hasManualSearchInput());
  clearSearchTimeout();

  searchTimeout = setTimeout(() => {
    runFetchMaterials();
  }, materialDebounceTime);
};

// Material Data Fetching

/**
 * Generic query for fetching materials.
 *
 * Searches based on:
 * 1) checkbox filters
 * 2) Sales Info fields filled out by user.
 */

const filteredMaterials = ref<Array<SkMaterial>>([]);
const materialResponseCount = ref(0);
// Number of rows the server returned for the current page *before* the
// quote-side unsellable filter is applied. Used to drive pagination so an
// all-filtered page does not falsely look like the end of the results.
const rawPageResultCount = ref(0);
const {
  executeQuery: fetchMaterials,
  fetching: materialsLoading
} = useSkMaterialsPaginatedQuery(
  {
    // Paused so URQL does not auto-execute on every reactive `variables` change.
    // All fetching is orchestrated imperatively via `runFetchMaterials` →
    // `fetchMaterials()`. Without this, toggling a filter triggered both the
    // reactive auto-run and the manual run, leaving one request aborted (the
    // red/erroring graphql call seen in the Network panel).
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      return {
        summitQueryString: createQueryString(queryParams.value)
      };
    })
  }
);

/**
 * This specific query needs all constraints determined by
 * queryParams (spec) with these additional modifications:
 *
 * 1) The contract field removed
 * 2) Just the materials to "restore" pricing that is missing
 * from the contract (for optimization  - this is a 2nd API call)
 */
const {
  data: skMaterialsCountry,
  executeQuery: fetchMaterialsCountry,
  fetching: materialsLoadingCountry
} = useSkMaterialsQuery(
  {
    // Paused; executed imperatively from `restoreMissingPrices` only. See the
    // paginated query above for the rationale (avoids duplicate/aborted calls).
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const _queryParamsCopy: MaterialQueryParams = structuredClone(queryParams.value)
      delete _queryParamsCopy.contract;

      // Shave down response time with this:
      _queryParamsCopy.number__in = filteredMaterials.value
        .filter((mat) => isContractPriceMissing(mat))
        .map((mat) => mat.number)
        .filter((mat_number): mat_number is string => !!mat_number && mat_number !== "custom");
      return {
        summitQueryString: createQueryString(_queryParamsCopy)
      };
    })
  }
);

const selectByNumber = (matNumber: string) => {
  const row = filteredMaterials.value.find(m => m.number === matNumber);
  if (row) {
    selectedMaterial.value = row as unknown as UIMaterial;
  }
};

const applyMaterialSearchResponse = (
  resp: Awaited<ReturnType<typeof fetchMaterials>>,
  fetchSequence: number,
  wantedNumber?: string
) => {
  const responseError = resp?.error?.value;
  if (responseError) {
    materialSearchError.value = responseError.message || "Material search failed. Please try again.";
  } else {
    materialSearchError.value = "";
  }

  const materialsResponse = resp?.data?.value?.getSKMaterialsPaginated;
  materialResponseCount.value = materialsResponse?.count || 0;
  const rawResults = cleanNull(materialsResponse?.results || []);
  rawPageResultCount.value = rawResults.length;
  // Quote-side stopgap: drop obsolete materials (and any extended only to
  // excluded plant IDs) before they reach the table. The "Search All Plants"
  // toggle otherwise limits results to the quote's plant server-side.
  let pageResults = filterUnsellableMaterials(rawResults).filter(material =>
    helpers.matchesMaterialStockFilters(material, filters)
  );
  // On the default (material number) view, prioritize in-stock materials by
  // floating them to the top of the page. Respect an explicit column sort.
  if (sortField.value === "number") {
    pageResults = sortInStockFirst(pageResults);
  }
  filteredMaterials.value = pageResults;
  // openPanelUpdateEvent();

  if (wantedNumber) {
    selectByNumber(wantedNumber);
  }

  // for dev testing: inject a test object into filteredMaterials
  const addTestObj = () => {  // eslint-disable-line
    const test_obj = {
      number: "12345678",
      long_description: "This is my custom test object",
      inventory: {
        available: "1.0",
        __typename: "SKInventory"
      },
      pricing: {
        price: "10000.0",
        __typename: "SKMaterialPricing"
      },
      serialized: true,
      __typename: "SKMaterial"
      // prod_hierarchy: "",
    } as SkMaterial;
    filteredMaterials.value.push(test_obj);
  };

  // for dev testing
  // addTestObj();
  setFromContractFlags();
  return restoreMissingPrices(fetchSequence);
};

const runFetchMaterials = (wantedNumber?: string) => {
  const fetchSequence = ++materialFetchSequence;

  // unselect selectedMaterial
  selectedMaterial.value = null;
  // Previous results no longer represent the current search or stock filters.
  filteredMaterials.value = [];
  materialResponseCount.value = 0;
  rawPageResultCount.value = 0;

  if (isMissingRequiredMaterialData.value) {
    materialSearchLoading.value = false;
    materialSearchError.value = "";
    return;
  }

  materialSearchLoading.value = true;
  materialSearchError.value = "";
  return Promise.resolve()
    .then(() => {
      if (filters.standardOfferingFilter && selectedEquipmentId.value) {
        return fetchQuoteLineItemsWithEquipment();
      }
    })
    .then(() => fetchMaterials())
    .then((resp) => {
      if (fetchSequence !== materialFetchSequence) {
        return;
      }

      return applyMaterialSearchResponse(resp, fetchSequence, wantedNumber);
    })
    .catch((error) => {
      if (fetchSequence === materialFetchSequence) {
        materialSearchError.value = error?.message || "Material search failed. Please try again.";
        filteredMaterials.value = [];
        materialResponseCount.value = 0;
        rawPageResultCount.value = 0;
      }
    })
    .finally(() => {
      if (fetchSequence === materialFetchSequence) {
        materialSearchLoading.value = false;
      }
    });
};

/**
 * Restores missing prices to the Material Lookup Table.
 */
const restoreMissingPrices = (fetchSequence?: number) => {
  const isAnyContractPriceMissing = filteredMaterials.value.some((mat) => isContractPriceMissing(mat));
  if (isAnyContractPriceMissing) {
    return Promise.resolve()
      .then(() => fetchMaterialsCountry())
      .then((resp) => {
        if (fetchSequence && fetchSequence !== materialFetchSequence) {
          return;
        }

        const countryMaterials: SkMaterial[] = cleanNull(resp?.data?.value?.getSKMaterials || []);
        const countryMaterialNumbers = countryMaterials.map((mat) => mat.number);

        // Quick in place modification:
        filteredMaterials.value
          .filter((mat) => countryMaterialNumbers.includes(mat.number))
          .forEach((mat) => {
            const countryMat = countryMaterials.find((cm) => cm.number === mat.number);
            if (countryMat?.pricing) {
              mat.pricing = countryMat.pricing; // <--- the modification
              (mat as any).from_contract = false;
            }
          });
      });
  }
  return Promise.resolve();
}

const setFromContractFlags = () => {
  // TODO: schema update might be better, change later
  filteredMaterials.value.forEach((mat: SkMaterial) => {
    (mat as any).from_contract = (
      isContractSelected.value
      && !isContractPriceMissing(mat)
      && isContractType(mat)
    );
  });
}


const resetStartX = () => {
  startX.value = null;

  // also reset side panel to original size
  setPanelWidth("520px");
};

onMounted(() => {
  window.addEventListener("resize", resetStartX);
});

onUnmounted(() => {
  clearSearchTimeout();
  window.removeEventListener("resize", resetStartX);
});


const sliderRef = ref<HTMLElement | null>(null);
const panelMinWidth = 300; // Minimum width
const panelMaxWidth = window.innerWidth * 0.8; // Maximum width (80% of screen)

const startX = ref<number | null>(0);
const initialPanelWidth = ref(0);
const panelWidth = ref(520); // Default width

const startResize = (e: MouseEvent) => {
  e.preventDefault(); // Prevent text selection while dragging
  startX.value = e.clientX;

  const sidePanel = document.querySelector(".side-panel") as HTMLElement;
  const rightContainer = document.querySelector(".right-vertical-container") as HTMLElement;

  // Make sure the panel is already visible before trying to resize
  if (!sidePanelOpen.value || !sidePanel) return;

  initialPanelWidth.value = sidePanel.offsetWidth;

  // Add cursor styling to indicate resizing
  (document.body as HTMLElement).style.cursor = 'ew-resize';

  // Disable transitions during dragging for smoother resizing
  sidePanel.style.transition = "none";
  if (rightContainer) {
    rightContainer.style.transition = "none";
  }

  document.addEventListener("mousemove", onResize);
  document.addEventListener("mouseup", stopResize);
};

const onResize = (e: MouseEvent) => {
  if (!startX.value) return;

  const deltaX = startX.value - e.clientX;
  let newWidth = initialPanelWidth.value + deltaX;

  // Clamp within min/max
  newWidth = Math.max(panelMinWidth, Math.min(panelMaxWidth, newWidth));

  const sidePanel = document.querySelector(".side-panel") as HTMLElement;
  const rightContainer = document.querySelector(".right-vertical-container") as HTMLElement;

  if (sidePanel) {
    sidePanel.style.width = `${newWidth}px`;
  }

  if (rightContainer) {
    //Add 1rem for example if vertical-slider-area width is 1rem and we add 1rem padding to right-vertical-container
    //We need to take this into account to make sure the arrow is positioned correctly
    // example: calc(${newWidth}px + 1rem)
    rightContainer.style.width = `calc(${newWidth}px)`;
  }

  setPanelWidth(`${newWidth}px`);
};

const stopResize = () => {
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);

  // Reset cursor
  (document.body as HTMLElement).style.cursor = '';

  // Re-enable transitions
  const sidePanel = document.querySelector(".side-panel") as HTMLElement;
  const rightContainer = document.querySelector(".right-vertical-container") as HTMLElement;
  
  if (sidePanel) {
    sidePanel.style.transition = "width 0.3s ease";
  }
  
  if (rightContainer) {
    rightContainer.style.transition = "width 0.3s ease";
  }
  
  startX.value = null;
};

// Child to EditQuote Emits (Calls Parent from Child)
const emit = defineEmits([
  "update:open-side-panel",
  "update:toggle-side-panel",
  "update:selected-material"
]);

const openPanelUpdateEvent = () => {
  emit("update:open-side-panel");
};
const togglePanelUpdateEvent = () => {
  emit("update:toggle-side-panel", {});
};
const triggerSelectedMaterialUpdate = (
  material: UIMaterial | null,
  action: string,
  line_item_type: string = ""
) => {
  if (material === null) {
    return null
  }

  material.line_item_type = line_item_type; // DetailsDataTable reads from this

  // Unsupported line item type checks:
  if (material.line_item_type === "vsd" || material.line_item_type === "switchboard") { // TODO: This can probably be added to the LineItemType enum
    // make 'VSD' + 'Switchboard' fall under => 'Surface Drive'
    material.line_item_type = LineItemType.SURFACE_DRIVE;
  } else if (material.line_item_type === LineItemType.PROTECTORS_SEALS) {
    // make 'Protectors Seals' (hierarchy) fall under => 'Seals' (equipment type)
    material.line_item_type = LineItemType.SEAL;
  } else if (material.line_item_type === LineItemType.TRANSFORMER) {
    // TODO let users move transformer (hierarchy) to step up or step down (equipment type)
    material.line_item_type = LineItemType.STEP_UP_TRANSFORMER;
  }

  // Serialized checks:
  if (
    isQuantityOneMaterial(
      material,
      equipmentTypeConversions.value?.getSKEquipmentTypeConversions || []
    )
  ) {
    // if serialized, lock quantity to 1 (many quote line items)
    material.quantity = 1;
  } else if (typeof material.serialized === "boolean") {
    material.quantity = addQuantity.value;
  }

  emit("update:selected-material", { material, action });
};

// EditQuote to Child Exposes (Calls Child from Parent)
// https://stackoverflow.com/questions/73725505/vue3-parent-component-call-child-component-method
// defineExpose({
//  () => {}
// })

const pricingData = computed(() => {
  return selectedMaterial?.value?.pricing;
});

const quoteIsWonOrRejected = computed(() => {
  return [statuses.won, statuses.rejected].some(
    status => status === quote?.value?.status
  );
});


/**
 * This returns a specific discount calculation needed for Ben's "subtotal".
 * @returns {number} - the difference of aggregated totals between of
 * (qty * ERP prices) and (qty * price overrides).
 */
const discountsForTotal = (quote_line_items: QuoteLineItemType[]) => {
  const _subtotal: number = subtotal(quote_line_items);
  const _totalOverride: number = totalOverride(quote_line_items);
  const discount: number = -_subtotal + _totalOverride;
  return discount;
};

/**
 * Returns the aggregated discounts of all line items regardless of type.
 * @returns {number} - the aggregate of discounts.
 */
const discounts: ComputedRef<number> = computed(() => {
  return getQuoteAdjustments(quote?.value?.quote_line_items ?? []).discounts;
});

/**
 * Return the total amount credited line items items regardless of its type.
 * @returns {number} - the absolute value of negative'd price overrides * quantity.
 */
const credits: ComputedRef<number> = computed(() => {
  return getQuoteAdjustments(quote?.value?.quote_line_items ?? []).credits;
});

/**
 * @returns {number} - the total margin represented as a percentage.
 */
const totalMargin = (quote_line_items: QuoteLineItemType[]): number => {
  const normalQuoteLineItems: QuoteLineItemType[] = quote_line_items
    ?.filter((qli: QuoteLineItemType) =>
      helpers.isNormalQuoteLineItem(qli)
    );

  if (normalQuoteLineItems.length === 0) {
    return 0.0;
  } else {
    const _totalProfit: number = total(normalQuoteLineItems) - totalCost(normalQuoteLineItems);
    const _total: number = total(normalQuoteLineItems)
    if (_total === 0) {
      // Cannot divide by 0. Show 0 instead of NaN or Infinity.
      return 0.0;
    } else {
      return (_totalProfit / _total) * 100;
    }
  }
};

/**
 * @returns {number} - the total AFTER discounts and credits
 */
const total = (quote_line_items: QuoteLineItemType[]): number => {
  let _total: number = 0;
  const _subtotal: number = subtotal(quote_line_items);
  const _discounts: number = discountsForTotal(quote_line_items);
  const _credits: number = 0;
  _total = _subtotal + _discounts + _credits;
  return _total;
};

/**
 * @returns {number} -  the total aggregate of qty * price override.
 * BEFORE discounts and credits.
 */
const totalOverride = (quote_line_items: QuoteLineItemType[]): number => {
  let unitSubtotalAggregate: number = 0;
  quote_line_items?.forEach((qli: QuoteLineItemType) => {
    // TODO: check if I need this bool (some are already checking this beforehand)
    const isNormalQli: boolean = helpers.isNormalQuoteLineItem(qli);
    if (isNormalQli) {
      const quantity = (notNull(qli.quantity) && qli.quantity > 0) ? qli.quantity : 1;
      unitSubtotalAggregate += getQuoteLineUnitPrice(qli) * Number(quantity);
    }
  });
  return unitSubtotalAggregate;
};

/**
 * @returns {number} - the total aggregate of qty * erp_price.
 * BEFORE discounts and credits.
 */
const totalCost = (quote_line_items: QuoteLineItemType[]): number => {
  let costSubtotalAggregate: number = 0;
  quote_line_items?.forEach((qli: QuoteLineItemType) => {
    const isNormalQli: boolean = helpers.isNormalQuoteLineItem(qli);
    if (isNormalQli) {
      const quantity = (notNull(qli.quantity) && qli.quantity > 0) ? qli.quantity : 1;
      costSubtotalAggregate += Number(qli.erp_price) * Number(quantity);
    }
  });

  return costSubtotalAggregate;
};

/**
 * This returns a calculated aggregate of STANDARD ERP prices * quantity.
 *
 * TODO: I think I should rename this to "standardSubtotal".
 * Here, the "standard" part probably refers to:
 *  - the hardcoded, unchanging, ERP price
 *  - the margin delta being 60 (decided by Ben, and/or by extension the business)
 *
 * @returns {number} - the aggregated standard subtotals.
 * BEFORE discounts and credits.
 */
const subtotal = (quote_line_items: QuoteLineItemType[]): number => {
  let standardSubtotalAggregate = 0;

  quote_line_items?.forEach((qli: QuoteLineItemType) => {
    const isNormalQli: boolean = helpers.isNormalQuoteLineItem(qli);
    if (isNormalQli) {
      const quantity = (notNull(qli.quantity) && qli.quantity > 0) ? qli.quantity : 1;
      const standardMargin: number = 60;
      const standardPrice: number = Number(qli.erp_price) / (1 - standardMargin / 100);
      const standardSubtotal: number = standardPrice * quantity;
      standardSubtotalAggregate += standardSubtotal;
    }
  });
  return standardSubtotalAggregate;
};

const _isJobTypeWellTestRental: ComputedRef<boolean> = computed(() => {
  return isJobTypeWellTestRental(quote?.value?.sk_job_type as Quote_Sk_Job_Type_Dataset_Enum)
})

/**
 * @returns {QuoteLineItemType[]} - all Quote Line Items (consumable + reusable).
 * Matches the PDF "Purchase Option" total which includes everything.
 */
const saleAndTradeLineItems: ComputedRef<QuoteLineItemType[]> = computed(() => {
  return quote?.value?.quote_line_items ?? [];
});

/**
 * @returns {QuoteLineItemType[]} - an array of Quote Line Items that are
 * consumable / not reusable. Use for isolated Sale & Trade calculations.
 */
 const wellTestLineItems: ComputedRef<QuoteLineItemType[]> = computed(() => {
  return quote?.value?.quote_line_items
    ?.filter((qli: QuoteLineItemType) => {
      return isConsumableWellTestLineItem(qli);
    });
});

/**
 * @returns {number} - the total margin of Sale & Trade / non-consumable line items.
 */
const saleAndTradeTotalMargin: ComputedRef<number> = computed(() => {
  return totalMargin(saleAndTradeLineItems.value);
});

/**
 * @returns {number} - the total cost of Sale & Trade / non-consumable line items.
 */
const saleAndTradeTotalCost: ComputedRef<number> = computed(() => {
  return totalCost(saleAndTradeLineItems.value);
});

/**
 * @returns {number} - the (sub?)total of Sale & Trade / non-consumable line items.
 * Factors in credits and discounts.
 */
const wellTestSubtotal: ComputedRef<number> = computed(() => {
  return total(wellTestLineItems.value);
});

/**
 * @returns {number} - the total margin of Sale & Trade / non-consumable line items.
 */
const wellTestTotalMargin: ComputedRef<number> = computed(() => {
  return totalMargin(wellTestLineItems.value);
});

/**
 * @returns {number} - the total cost of Sale & Trade / non-consumable line items.
 */
const wellTestTotalCost: ComputedRef<number> = computed(() => {
  return totalCost(wellTestLineItems.value);
});

/**
 * Dev note: in business contexts, subtotal usually refers to BEFORE credits
 * and discounts.
 *
 * However, Ben wants this specific label to be "Subtotal", and this computed
 * value is factoring in credits and discounts (with the final calculations signed
 * off by him). Just keep that in mind when trying to understand these functions.
 *
 * @returns {number} - the (sub?)total of Sale & Trade / non-consumable line items.
 * Factors in credits and discounts.
 */
const saleAndTradeSubtotal: ComputedRef<number> = computed(() => {
  return total(saleAndTradeLineItems.value);
});

const getNumberOfResults = computed(() => {
  return materialResponseCount.value;
});

const notesBackgroundColor = computed(() => {
  // format: "{thumb color} {background color}"
  return settingsStore.isDarkMode ? "rgb(27, 28, 31)" : "white";
});
const notesColor = computed(() => {
  return settingsStore.isDarkMode ? "white" : "rgb(27, 28, 31)";
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(getNumberOfResults.value / filters.pageSize));
});

const {
  foundEtc,
  materialEquipmentType,
  computedLineItemType,
  equipmentTypeConversions,
} = useEquipmentTypeConversions(selectedMaterial);

// Product Hierarchy options sourced from SK's Equipment Type Conversion table.
// The dropdown shows a human-friendly equipment-type label; the value is the
// comma-joined list of that type's product hierarchy numbers, which is sent to
// the search as prod_hierarchies behind the scenes. Entries are grouped by
// label so ECC/S4 duplicates collapse into one option (their hierarchy numbers
// are unioned).
const productHierarchyChoices = computed(() => {
  const etcs = equipmentTypeConversions?.value?.getSKEquipmentTypeConversions || [];
  const byLabel = new Map<string, Set<string>>();

  for (const etc of etcs) {
    const label = (etc?.label || etc?.equipment_type || "").trim();
    const numbers = (etc?.hierarchy_numbers || []).filter(
      (n): n is string => !!n && n.trim() !== ""
    );
    if (!label || numbers.length === 0) {
      continue;
    }
    if (!byLabel.has(label)) {
      byLabel.set(label, new Set<string>());
    }
    const bucket = byLabel.get(label) as Set<string>;
    numbers.forEach((n) => bucket.add(n.trim()));
  }

  const options = [...byLabel.entries()]
    .map(([label, numbers]) => ({ label, value: [...numbers].join(",") }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Leading empty-value option clears the filter while keeping the model a
  // string (avoids null from showClear, which buildMaterialSearchQueryParams
  // would otherwise have to guard against).
  return [{ label: "Any Equipment Type", value: "" }, ...options];
});
</script>

<style scoped lang="scss">
:deep {
  // TODO: implement re-usable pattern in parent EditQuote.vue
  // (see DetailsHeaderForm.vue for pattern usage)
  .fixed-inner-label.add-quantity-label {
    white-space: nowrap; // prevents label wrapping if too long
    text-indent: 0; // prevents label wrapping if too long
    user-select: none; // fixes outside of label from being selectable/highlightable

    label {
      user-select: none;
      pointer-events: none;
    }
  }

  .toggle-panel-button-background .toggle-panel-button .p-button-icon.pi,
  .toggle-panel-button-background .toggle-panel-button .p-button-icon.pi:before,
  .toggle-panel-button-background:hover .toggle-panel-button .p-button-icon.pi,
  .toggle-panel-button-background:hover .toggle-panel-button .p-button-icon.pi:before {
    color: var(--gray-100) !important;
  }

  // Panel Text Areas
  textarea.panel-text-area {
    width: 95%;
    margin-left: 12px;
    height: 150px;
    margin-top: 1.5rem;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-left: 10px;
    padding-right: 10px;
    resize: none;
  }

  .panel-text-label {
    margin-top: 2rem;
  }

  div.panel-text-label .form-label-text {
    display: inline-block;
    color: var(--info-600);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  // Panel Text Areas End
}

.side-panel {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex-wrap: nowrap;
  float: right;
  position: relative;
  color: var(--text-color);
  background-color: var(--surface-50);
  border-left: 4px solid var(--danger-500);
  box-shadow: -4px 0 12px rgb(0 0 0 / 12%);
  z-index: 1;
  flex-grow: 0;
  flex-shrink: 0;
  max-width: 100%;
  top: 0;
  bottom: 0;
  right: 0;
  padding: 0 0rem;
  
  transition: 0.3s ease;
}

.padded-section {
  padding: 1em;
  margin: 0.75rem 1rem 0.25rem;
  gap: 0.4rem 0.75rem;
  background: var(--surface-25);
  border: 1px solid var(--border-main);
  border-radius: 4px;
}

.padded-section-item {
  margin-top: 2px;
  margin-bottom: 2px;
  width: 100%;
}

.material-filters {
  display: flex;
  padding: 0 1rem;
  gap: 0.5rem;
}

.material-search {
  display: inline-block;
  flex: 2;
}

.material-search-exclude {
  display: inline-block;
  flex: 1;
  min-width: 80px;
}

.product-hierarchy-filter {
  padding-top: 0.5rem;
}

/* Material Type sits inline with the other filters (no surrounding box). */
.material-type-row {
  padding: 0.75rem 1rem 0;
}

.material-type-row .material-type-label {
  display: block;
  margin-bottom: 4px;
}

.material-search-error {
  background-color: var(--danger-100);
  border: 1px solid var(--danger-500);
  border-radius: 4px;
  color: var(--danger-700);
  font-weight: 600;
  margin: 0.5rem 1rem;
  padding: 0.75rem;
}

.page-size-control select {
  margin-left: 0.4rem;
}

.search-label {
  color: var(--info-600);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.side-panel .top-level {
  display: flex;
  align-items: center;
}

/* Header bar decorations only when expanded (avoids broken look when collapsed) */
.side-panel.side-panel-open .top-level {
  background: var(--gradient-vertical);
  border-bottom: 2px solid var(--danger-500);
}

.side-panel .toggle-button {
  margin-top: 20px;
  height: 10px;
  width: 10px;
  cursor: pointer;
  z-index: 10;

  transition: 0.2s linear;
}

/* Transition for Side Panel Toggle Button */
/*
.side-panel .toggle-button:hover {
  transform: rotate(180deg);
}
.side-panel .toggle-button::before {
  -webkit-transform-origin: center center;
  -moz-transform-origin: center center;
  -o-transform-origin: center center;
  transform-origin: center center;
}
*/

.toggle-panel-button-background {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 17px 0 37px 0;
  visibility: visible !important;
}

/*
.toggle-panel-button {
  color: var(--gray-200);
  font-size: 24px;
  cursor: pointer;
  user-select: none;
}
.toggle-panel-button:hover {
  color: var(--gray-200);
}
*/

/* Branded expand/collapse control: a circular button using the panel's red
   accent (matching the left border, header underline, and table header rule)
   instead of the generic light secondary button. */
.toggle-panel-button.p-button {
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background: var(--danger-500) !important;
  border: 1px solid var(--danger-600) !important;
  box-shadow: 0 1px 4px rgb(0 0 0 / 30%);
  transition: filter 0.15s ease, transform 0.15s ease;
}

.toggle-panel-button.p-button:hover {
  background: var(--danger-500) !important;
  border-color: var(--danger-600) !important;
  filter: brightness(1.1);
}

.toggle-panel-button.p-button:focus {
  box-shadow: 0 0 0 2px var(--surface-50), 0 0 0 4px var(--danger-400);
}

.toggle-panel-button > span.pi {
  cursor: pointer;
  user-select: none;
  font-size: 1.1rem;
  color: #fff !important;
}

.pi.pi-angle-right,
.pi.pi-angle-left {
  color: var(--gray-100);
}


.toggle-panel-button-background-open {
  margin: 17px;
  align-content: center;
}

.side-panel .header {
  margin: 0;
  align-content: center;
  color: var(--text-color);
  font-weight: 700;
  letter-spacing: 0.3px;
}

.side-panel.side-panel-open,
.side-panel.side-panel-closed {
  width: 100%;
  /*width: 54px;*/ /* was closed only */
}

.side-panel .selected {
  font-weight: 700;
  font-size: 14px;
}

.side-panel input .p-checkbox-box {
  width: 17px;
  height: 17px;
}

.add-to-quote-buttons {
  display: flex;
  margin-left: 12px;
  margin-right: 20px; // seems to be hardcoded for now
  gap: 10px;
}

.add-to-quote-buttons > button {
  /* Currently overriden by spyglass `.p-button { border: 0 }` */
  max-width: 200px;
  min-width: 70px;
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  flex: 1 1 auto; /* Last button will respect width of side panel */

  /* Css intended for child - but can't target text nodes */
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  display: inline-block; // center aligns text (inner width was not 100%)
  transition: filter 0.15s ease;
}

/* Add To Quote - primary action (Spyglass blue) */
.add-to-quote-buttons > button:nth-of-type(1) {
  background: var(--info-600) !important;
  border-color: var(--info-700) !important;
}

/* Replace Selected - warning accent (matches header offline indicator) */
.add-to-quote-buttons > button:nth-of-type(2) {
  background: var(--warning-500) !important;
  border-color: var(--warning-600) !important;
}

/* Add Custom Line - success accent (matches header online/wifi indicator) */
.add-to-quote-buttons > button:nth-of-type(3) {
  background: var(--success-500) !important;
  border-color: var(--success-600) !important;
}

.add-to-quote-buttons > button:not(:disabled):hover {
  filter: brightness(1.08);
}

.add-to-quote-buttons > button:disabled {
  background: var(--surface-200) !important;
  border-color: var(--surface-400) !important;
  color: var(--text-color) !important;
  opacity: 0.7;
  cursor: not-allowed;
}

.side-panel h3.main-title {
  color: var(--text-color);
  margin-bottom: 0;
}

/*
  .side-panel-container input.p-autocomplete-input.p-inputtext.p-component {
    width: 100%;
  }
  */

.highcharts-container {
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 0;
  padding-right: 0;
  --margin-top: 15px;
}

/* Material Data Table Title */
.required-info h5 {
  display: flex;
  align-items: center;
  margin: 4px 0;
  padding: 12px;
  border: 2px solid var(--danger-500);
  border-radius: 4px;
  background-color: var(--danger-100);
  color: var(--danger-900);
  font-weight: 700;

  span {
    margin-left: 10px;
  }
}

h4.material-lookup-title {
  color: var(--text-color);
  left: 8px;
  margin-bottom: 14px;
  margin-left: 12px;
  padding: 15px 0 8px 0;
  font-size: 20px;
  font-weight: 700;
}

/* Selected Label */
.selected-list {
  margin: 18px 0 5px;
  padding-left: 17px;
}

.selected-list > li {
  color: var(--text-color);
  font-size: 1em;
  font-weight: 500;
  list-style-type: none;
}

.selected-list li span.selected-material-number {
  background-color: var(--info-500);
  color: #fff;
  border-radius: 3px;
  padding: 3px 8px;
  margin-left: 10px;
  font-weight: 700;
}

.selected-list span.selected-material-number {
  background-color: var(--info-500);
  padding: 3px 8px;
  margin-left: 8px;
  color: #fff;
}

.selected-list .selected-list-li {
  margin-bottom: 5px;
}

.selected-list .selected-list-li .equipment-type-label {
  margin-left: 10px;
}

/* Paginator Styles */
.p-paginator {
  display: flex;
  align-items: center;
  justify-content: center;

  .p-paginator-prev,
  .p-paginator-next {
    padding: 11px;
  }

  .p-paginator-current {
    padding-top: 9px;
    color: var(--text-color);
  }
}

.paginator-button {
  color: var(--text-color);
}

.paginator-button-disabled {
  color: var(--surface-500);
  padding: 11px;
}
/* End Paginator Styles*/

.side-panel-closed-header {
  color: var(--text-color);
  font-weight: 600;
  padding: 6px;
  transform: rotate(90deg);
}

/* Side Panel Slider - changes min/max width dynamically */
.slider-icon {
  position: absolute;
  left: 0; /* needs to scale with widening of side panel */
  top: 345px;
  z-index: 9; /* must be lower than area index */
  color: var(--text-color);
  display: none;
}

.vertical-slider-area {
  display: block;
  height: 100%;
  width: 0.55rem;
  cursor: ew-resize;
  z-index: 10;
  position: absolute;
  left: 0;
}

.vertical-slider-area:hover ~ i.slider-icon,
.vertical-slider-area:active ~ i.slider-icon {
  display: block;
}

/* Side Panel Slider End*/

/* Panel Data Views */
.panel-data-view {
  width: 95%;
  margin-left: 12px;
  margin-right: 12px;
  resize: none;
  border: 1px solid var(--border-main);
  border-left: 4px solid var(--info-500);
  border-radius: 4px;
  background: var(--surface-25);
  box-shadow: var(--shadow-main);
}

.panel-data-view-inner {
  padding-top: 0.6em;
  padding-bottom: 0.6em;
  overflow-x: auto;
}

.panel-data-header {
  color: var(--text-color);
  margin-top: 2rem;
  margin-left: 15px;
  display: inline-block;
  font-weight: 700;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.panel-data-subheader {
  margin-left: 12px;
  margin-bottom: 5px;
  text-transform: uppercase;
  font-weight: bold;
  color: var(--info-600);
  letter-spacing: 0.4px;
}

.panel-data-row {
  display: flex;
  gap: 1.8rem;
  margin-left: 12px;
  margin-bottom: 25px;
  color: var(--text-color);
}

.panel-data-row:last-child {
  margin-bottom: 0; /* Remove margin-bottom for the last element */
}

.panel-data-data {
  font-weight: bold;
  font-size: 1rem;
  color: var(--text-color);
}

/* Choose Type Dropdown Styles */
.choose-type-dropdown span.p-dropdown-label.p-inputtext {
  font-size: 15px;
}

.add-quantity-dropdown,
.choose-type-dropdown {
  /*height: 3rem;*/
  margin-bottom: 20px;
}

.add-quantity-dropdown {
  width: 4rem;
}

.choose-type-label {
  margin-left: 12px;
  padding-left: 0;
}
.add-quantity-label {
  margin-right: 20px;
  padding-top: 9px; /* Move quanity input down */
}

.choose-type-flex {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
}
/* Choose Type Dropdown End */

.right-align {
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
}

/* Hide but still in flow (does not move other elements) */
.invisible {
  visibility: hidden;
}

.side-panel .p-paginator .paginator-controls {
  color: var(--text-color);
}
</style>

<style lang="scss">
/* Scoped caused this css to not apply */
#materialLookupTable .p-datatable-table tbody td.data-column {
  padding: 3px;
}

/* Change Header Text */
/* Change Header Background */
#materialLookupTable thead.p-datatable-thead tr > th {
  color: var(--text-color);
  background-color: var(--surface-200);
}

#materialLookupTable .p-datatable-header {
  background-color: unset;
}

/* Material Table Header Colors */
#materialLookupTable thead.p-datatable-thead tr > th {
  background-color: var(--surface-200);
  color: var(--text-color);
  font-weight: bold;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  padding-left: 0.5em;
  padding-right: 0.5em;
  border-bottom: var(--danger-500) 2px solid;
}

/* Material Table Row Colors */
#materialLookupTable tbody.p-datatable-tbody tr {
  > td {
    border: none;
  }

  .data-column {
    color: var(--text-color);
    padding-left: 0.5em;
    padding-right: 0.5em;
    padding-bottom: 0.4em;
    padding-top: 0.4em;
  }
}

/* Pagination Colors */
#materialLookupTable .p-paginator {
  .p-paginator-current,
  .p-paginator-first,
  .p-paginator-prev,
  .p-paginator-pages,
  .p-paginator-next,
  .p-paginator-last {
    color: var(--text-color);
  }
}

/* Auto Complete No Results Message */
#materialLookupTable .p-autocomplete-items li.p-autocomplete-empty-message {
  padding: 2px;
  font-size: 14px;
  font-weight: 400;
  margin-left: 8px;
}

#customLineItemInterface thead.p-datatable-thead tr > th {
  color: var(--surface-100);
  background-color: var(--surface-900);
}

#customLineItemInterface span.p-dropdown-label.p-inputtext,
#customLineItemInterface input.p-inputtext {
  padding-left: 8px;
}

#customLineItemInterface input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

#customLineItemInterface .input-symbol-usd {
  cursor: text;
  position: relative;
}
#customLineItemInterface .input-symbol-usd input {
  padding-left: 16px;
}
#customLineItemInterface .input-symbol-usd:before {
  position: absolute;
  top: 0px;
  left: 8px;
  content: "$";
  color: var(--text-color); // Prevents row select from inverting the color
  pointer-events: none; // Allows selection of the underlying primevue component
}

/*
#materialLookupTable .standrad-offering tr.p-selectable-row.p-row-even {
  background-color: rgb(5, 54, 15);
}
#materialLookupTable .standard-offering tr.p-selectable-row.p-row-odd {
  background-color: rgb(11, 77, 28);
}
*/
.pi.pi-check-circle.standard-offering-checkbox {
  color: #25db34;
  position: relative;
  right: -15px;
}
</style>
